import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { apiErrorText } from "@/lib/apiError";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import {
  Building2,
  CheckCircle2,
  Clock,
  Loader2,
  Mail,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";

/**
 * /uni/:id/claim — public entrypoint for a university staff member.
 *
 * Flow:
 *  1. Signed out → "Sign in with Google" CTA.
 *  2. Signed in → shows the uni card + role input + Submit claim.
 *  3. Server auto-approves if the user's email domain matches; otherwise
 *     puts the claim in the admin queue and shows a "pending" panel.
 */
export default function UniClaim() {
  const { id } = useParams();
  const { user, loading: authLoading, login } = useAuth();
  const [uni, setUni] = useState(null);
  const [claimState, setClaimState] = useState(null); // {status, ...}
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  // Magic-link sub-flow (visible only when signed-out).
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSending, setMagicSending] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [u, c] = await Promise.all([
          api.get(`/universities/${id}`).catch(() => ({ data: null })),
          api.get(`/claims/uni/${id}`).catch(() => ({ data: null })),
        ]);
        setUni(u.data);
        setClaimState(c.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/claims/submit", {
        university_id: id,
        role: role || null,
      });
      setClaimState({ ...claimState, latest: data });
      if (data.status === "approved") {
        toast.success("You're verified — welcome to your portal");
      } else if (data.status === "already_owner") {
        toast.info("You already own this university");
      } else {
        toast.success("Claim submitted for review");
      }
    } catch (err) {
      toast.error(apiErrorText(err, "Could not submit claim"));
    } finally {
      setSubmitting(false);
    }
  };

  const sendMagicLink = async (e) => {
    e?.preventDefault?.();
    const email = magicEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter your work email address.");
      return;
    }
    setMagicSending(true);
    try {
      await api.post("/auth/magic-link/request", {
        email,
        university_id: id,
      });
      setMagicSent(true);
    } catch (err) {
      // The endpoint always returns 200 for non-shape errors, so the
      // only way we land here is a validation 422 (bad email format)
      // — surface it plainly.
      toast.error(apiErrorText(err, "Couldn't send just now — try again."));
    } finally {
      setMagicSending(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-stone-500 font-mono text-sm" data-testid="uni-claim-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }

  if (!uni) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-stone-600" data-testid="uni-claim-notfound">
        We couldn't find that university.{" "}
        <Link to="/explore" className="underline">Browse the guide</Link>
      </div>
    );
  }

  const latest = claimState?.latest;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="uni-claim-page">
      <Link to={`/uni/${id}`} className="text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-900">
        ← Back to {uni.name}
      </Link>

      <div className="mt-4 p-8 border border-stone-300 rounded-xl bg-white">
        <div className="inline-flex items-center gap-2 text-[10px] font-underground-sc tracking-widest text-stone-500">
          <ShieldCheck className="h-3 w-3" /> University Portal
        </div>
        <h1 className="mt-3 font-editorial text-4xl leading-tight">
          Claim {uni.name}.
        </h1>
        <p className="mt-3 text-stone-600 text-base leading-relaxed max-w-xl">
          Verified staff can correct course data, tuition, offer rates and
          entry requirements. Edits go live immediately with a "verified by
          university" mark next to every field you own.
        </p>

        {!user ? (
          <div className="mt-6 space-y-5" data-testid="uni-claim-signin-panel">
            <div>
              <Button
                onClick={login}
                data-testid="uni-claim-signin-btn"
                className="w-full sm:w-auto"
              >
                Sign in with Google <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <p className="mt-2 text-[11px] font-underground-sc tracking-wider text-stone-400">
                Best if your university uses Google Workspace (e.g.
                <span className="font-mono"> @ox.ac.uk</span>) — we
                auto-approve on domain match.
              </p>
            </div>

            <div
              className="flex items-center gap-3 text-[10px] font-underground-sc tracking-widest text-stone-400"
              aria-hidden="true"
            >
              <div className="flex-1 h-px bg-stone-200" />
              <span>or</span>
              <div className="flex-1 h-px bg-stone-200" />
            </div>

            {magicSent ? (
              <div
                className="p-4 rounded-md border border-emerald-200 bg-emerald-50"
                data-testid="uni-claim-magic-sent-panel"
              >
                <div className="flex items-center gap-2 text-emerald-800">
                  <Mail className="h-4 w-4" />
                  <span className="font-editorial text-base">
                    Check your inbox.
                  </span>
                </div>
                <p className="mt-1 text-sm text-emerald-900">
                  We've emailed a sign-in link to{" "}
                  <span className="font-mono">{magicEmail}</span>. It
                  expires in 15 minutes and only works once. If you
                  don't see it, check spam or request a new one in a
                  minute.
                </p>
                <button
                  type="button"
                  onClick={() => { setMagicSent(false); setMagicEmail(""); }}
                  className="mt-3 text-[11px] font-underground-sc tracking-wider text-emerald-800 hover:underline"
                  data-testid="uni-claim-magic-again-btn"
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form
                onSubmit={sendMagicLink}
                data-testid="uni-claim-magic-form"
                className="space-y-2"
              >
                <label
                  htmlFor="magic-email"
                  className="text-[11px] font-underground-sc tracking-wider text-stone-600 flex items-center gap-1"
                >
                  <Mail className="h-3 w-3" /> Email me a sign-in link
                </label>
                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                  <Input
                    id="magic-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@your-university.edu"
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    data-testid="uni-claim-magic-email-input"
                    maxLength={200}
                    className="flex-1 min-w-[220px]"
                    required
                  />
                  <Button
                    type="submit"
                    disabled={magicSending || !magicEmail.trim()}
                    data-testid="uni-claim-magic-submit-btn"
                    variant="outline"
                  >
                    {magicSending ? "Sending…" : "Email me the link"}
                  </Button>
                </div>
                <p className="text-[11px] font-underground-sc tracking-wider text-stone-400">
                  Works with any institutional inbox (Microsoft 365,
                  Google Workspace, your own mail server). Public
                  providers (gmail, outlook.com) aren't accepted.
                </p>
              </form>
            )}
          </div>
        ) : latest?.status === "approved" || claimState?.owners?.some((o) => o.name && user?.name && o.name === user.name) ? (
          <div className="mt-6 p-5 rounded-md border border-emerald-200 bg-emerald-50" data-testid="uni-claim-approved-panel">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-editorial text-lg">You're verified.</span>
            </div>
            <p className="mt-2 text-sm text-emerald-900">
              You can now edit {uni.name} and its degrees.
            </p>
            <div className="mt-3 flex gap-3">
              <Link to="/uni-portal">
                <Button size="sm" data-testid="uni-claim-open-portal-btn">
                  Open University Portal
                </Button>
              </Link>
              <Link to={`/uni/${id}`}>
                <Button size="sm" variant="outline">
                  View public page
                </Button>
              </Link>
            </div>
          </div>
        ) : latest?.status === "pending" ? (
          <div className="mt-6 p-5 rounded-md border border-amber-200 bg-amber-50" data-testid="uni-claim-pending-panel">
            <div className="flex items-center gap-2 text-amber-900">
              <Clock className="h-5 w-5" />
              <span className="font-editorial text-lg">
                Claim under review.
              </span>
            </div>
            <p className="mt-2 text-sm text-amber-900">
              We couldn't auto-verify your email domain against{" "}
              <span className="font-mono">{uni.name}</span>'s allowlist.
              An editor will decide within one working day and drop you a
              line at <span className="font-mono">{user.email}</span>.
            </p>
          </div>
        ) : latest?.status === "already_owner" ? (
          <div className="mt-6 p-5 rounded-md border border-emerald-200 bg-emerald-50" data-testid="uni-claim-already-panel">
            <div className="flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="h-5 w-5" />
              <span>You already own this university.</span>
            </div>
            <div className="mt-3">
              <Link to="/uni-portal">
                <Button size="sm">Open University Portal</Button>
              </Link>
            </div>
          </div>
        ) : (
          <div className="mt-6" data-testid="uni-claim-form-panel">
            <div>
              <label className="text-[11px] font-underground-sc tracking-wider text-stone-500">
                Your role at {uni.name} <span className="normal-case text-stone-400">(optional)</span>
              </label>
              <Input
                className="mt-1"
                placeholder="e.g. Head of Admissions"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                data-testid="uni-claim-role-input"
                maxLength={120}
              />
            </div>
            <div className="mt-4 text-xs text-stone-600 flex items-start gap-2">
              <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Signed in as <span className="font-mono">{user.email}</span>.
                {claimState?.has_domains ? (
                  <> If this email is on the university's official domain, you'll be verified instantly.</>
                ) : (
                  <> We haven't set an auto-approve domain for this university yet — an editor will review your claim.</>
                )}
              </span>
            </div>
            <Button
              className="mt-5"
              onClick={submit}
              disabled={submitting}
              data-testid="uni-claim-submit-btn"
            >
              {submitting ? "Submitting…" : "Submit claim"}
            </Button>
          </div>
        )}
      </div>

      {claimState?.claimed && (
        <div className="mt-6 p-5 border border-stone-200 rounded-lg bg-stone-50" data-testid="uni-claim-existing-owners">
          <div className="text-[11px] font-underground-sc tracking-widest text-stone-500">
            Existing verified owners
          </div>
          <ul className="mt-2 text-sm text-stone-700 space-y-1">
            {claimState.owners.map((o, i) => (
              <li key={i}>
                {o.name}
                {o.role ? <span className="text-stone-500"> · {o.role}</span> : null}
                <span className="text-[11px] font-underground-sc text-stone-400 ml-2">
                  {o.verified_via === "domain" ? "domain-verified" : "editor-verified"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
